import { type FC } from 'react'

import {
  contentSection,
  menuCoverImage,
  mutedText,
  offlineText,
} from '@liveHelper/components/Idle/Idle.twind'
import { expandedHeaderImageContainer } from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useStartScheduling } from '@liveHelper/hooks/useStartScheduling'
import {
  actionsSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { Close } from '@oneToOne/shared/assets/Close'
import { ScheduleSVG } from '@oneToOne/shared/assets/ScheduleSVG'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding/FireworkBranding'

import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

/**
 * Idle expanded state with schedule only button when waitlist capacity is reached
 */
export const IdleExpandedScheduleOnly: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const { lang } = useStore(paramsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()
  const startScheduling = useStartScheduling()

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Conversation`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label="close" />}
      />
      <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
        <div
          className={tw?.(
            menuCoverImage(
              chatChannelConfig?.widget_style?.menu_cover_image_url,
            ),
          )}
        ></div>
      </div>
      <div className={tw?.(contentSection, 'px-10', 'pt-10', 'pb-7')}>
        <div
          className={tw?.(
            'flex',
            'flex-col',
            'justify-center',
            'items-center',
            'gap-2.5',
          )}
        >
          <span className={tw?.(offlineText, 'text-lg', 'font-semibold')}>
            {t('oneToOne:We are busy at the moment')}
          </span>
          <span className={tw?.(mutedText, 'text-sm', 'text-center')}>
            {t(
              `oneToOne:Demand is high right now, we will be right with you. Schedule a call with us.`,
            )}
          </span>
          <Button
            className={tw?.('mt-4')}
            aria-label={t(`oneToOne:Book now`)}
            onClick={startScheduling}
            block
          >
            <div className={tw?.('flex', 'gap-2', 'items-center')}>
              <ScheduleSVG
                height="14px"
                width="14px"
                fill="var(--white)"
                label="schedule"
              />
              {t(`oneToOne:Schedule`)}
            </div>
          </Button>
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding dark={true} utmContent={utmContent} lang={lang} />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
